import i18next, { type ParseKeys } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type {} from './i18next-types';
import { DEFAULT_LANGUAGE, isLanguage, saveLanguage, type Language } from './languages';
import en from './locales/en.json';
import vi from './locales/vi.json';

export { useTranslation, Trans } from 'react-i18next';
export { formatMoney, formatNumber, formatTime, formatDate, formatDateTime } from './format';
export { quickCashAmounts } from './money';
export { fold } from './text';
export {
  configureLanguageStorage,
  DEFAULT_LANGUAGE,
  detectLanguage,
  isLanguage,
  LANGUAGES,
  localeOf,
  saveLanguage,
  savedLanguage,
  type Language,
  type LanguageStorage,
} from './languages';

/** A translation key; checked at compile time. */
export type I18nKey = ParseKeys;

/**
 * Initialise translations once per app. Item and category names are not here: they come from the database, with
 * translations in `name_i18n` (see localized()).
 */
export function initI18n(language: Language = DEFAULT_LANGUAGE) {
  if (!i18next.isInitialized) {
    void i18next.use(initReactI18next).init({
      resources: { vi: { translation: vi }, en: { translation: en } },
      lng: language,
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
      returnNull: false,
    });
    i18next.on('languageChanged', (lng) => {
      if (typeof document !== 'undefined') document.documentElement.lang = lng;
    });
    if (typeof document !== 'undefined') document.documentElement.lang = language;
  } else if (i18next.language !== language) {
    void i18next.changeLanguage(language);
  }
  return i18next;
}

/** Switches the UI language now and remembers it on this device. */
export function setLanguage(language: Language) {
  saveLanguage(language);
  initI18n(language);
}

/** The UI language in use; sent to the backend as Accept-Language. */
export function currentLanguage(): Language {
  const language = i18next.language;
  return isLanguage(language) ? language : DEFAULT_LANGUAGE;
}

/**
 * Translated message for a backend error code (ProblemDetails `code`), with a safe fallback. Pass the count the message
 * will be translated with when the code's text has plural forms only (`…_one`, `…_other`), such as a close blocker.
 */
export function errorMessageKey(code: string | undefined, count?: number): I18nKey {
  const key = `errors.${code}`;
  return (code && i18next.exists(key, count === undefined ? undefined : { count }) ? key : 'errors.unknown') as I18nKey;
}

/**
 * The message for a failed call: our own translation of the error code, else the backend's title (already in the
 * request's language, ADR 0007), else a generic message.
 */
export function problemText(code: string | undefined, title: string | null | undefined): string {
  if (code && i18next.exists(`errors.${code}`)) return String(i18next.t(`errors.${code}` as I18nKey));
  return title || String(i18next.t('errors.unknown'));
}

/**
 * Why a discount or charge can't be used (ADR 0039): a rule's reason code (`rules.guest_count`, `rules.sale_type_excluded`...)
 * with the values the backend sent to explain it. Undefined for anything that isn't a rule's reason.
 */
export function ruleReasonText(reason: unknown, values?: unknown): string | undefined {
  if (typeof reason !== 'string' || !reason.startsWith('rules.')) return undefined;
  const name = reason.slice('rules.'.length);
  const key = i18next.exists(`rules.reason.${name}`)
    ? `rules.reason.${name}`
    : name.endsWith('_excluded')
      ? 'rules.reason.excluded'
      : 'rules.reason.evaluation_failed';
  // The key is built at run time, so the typed overloads of t can't check it.
  const translate = i18next.t.bind(i18next) as (key: string, options: Record<string, unknown>) => string;
  return translate(key, (values ?? {}) as Record<string, unknown>);
}

/** Text from a `*_i18n` column (`{"vi": "...", "en": "..."}`) in the UI language, else the plain column. */
export function localized(texts: Partial<Record<string, string>> | null | undefined, fallback: string, language = currentLanguage()) {
  return texts?.[language] || fallback;
}
